import { Component, createEffect, createMemo, createSignal, on, Show } from 'solid-js'
import { A, useLocation, useNavigate, useSearchParams } from '@solidjs/router'
import Alert from '../../shared/Alert'
import Divider from '../../shared/Divider'
import PageHeader from '../../shared/PageHeader'
import { ACCOUNT_KEY, settingStore, toastStore, userStore, i18nStore } from '../../store'
import { setComponentPageTitle, storage } from '../../shared/util'
import TextInput from '../../shared/TextInput'
import Button from '../../shared/Button'
import { isLoggedIn } from '/web/store/api'
import { TitleCard } from '/web/shared/Card'
import { Page } from '/web/Layout'
import { useGoogleReady } from '/web/shared/hooks'
import { createStore } from 'solid-js/store'
import { wait } from '/common/util'

const LoginPage: Component = () => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  setComponentPageTitle(i18n.t.login)
  const store = userStore((s) => ({ error: s.error, loggedIn: s.loggedIn, loading: s.loading }))
  const cfg = settingStore((s) => ({ config: s.config }))
  const [query] = useSearchParams()

  const [register, setRegister] = createSignal(false)

  /** Friendly error message passed out of the mutation, if it exists. */
  const loginError = createMemo(() => {
    if (!store.error) return null
    if (store.error.includes('NetworkError')) {
      return i18n.t.couldNotReachServers
    }

    return i18n.t.somethingWentWrong
  })

  return (
    <Page class="flex w-full flex-col items-center">
      <div class="my-4 border-b border-white/5" />
      <PageHeader
        title={
          <div class="flex w-full justify-center">
            <Show when={query.callback} fallback={i18n.t.welcomeToAccount}>
              {i18n.t.authorizing}
            </Show>
          </div>
        }
        subtitle={
          <div class="flex flex-wrap items-center justify-center">
            <Show when={store.loggedIn}>{i18n.t.youAreAlreadyLoggedIn}</Show>
            <Show when={!store.loggedIn}>
              <Button size="pill" onClick={() => setRegister(false)}>
                {i18n.t.login}
              </Button>
              &nbsp; {i18n.t.toYourAccount} &nbsp;
              <Button size="pill" onClick={() => setRegister(true)}>
                {i18n.t.register}
              </Button>
              &nbsp;{i18n.t.orContinueAsGuest}
            </Show>
          </div>
        }
      />
      <div class="w-full max-w-sm">
        <Show when={register()}>
          <RegisterForm isLoading={store.loading} />
        </Show>
        <Show when={!register()}>
          <LoginForm isLoading={store.loading} />
        </Show>
        <Show when={loginError()}>
          <Divider />
          <Alert schema="error" title={i18n.t.failedToLogIn}>
            {loginError()}
          </Alert>
        </Show>
      </div>

      <Show when={cfg.config.policies}>
        <div class="mt-2">
          {i18n.t.byLoggingIn}{' '}
          <A class="link" href="/terms-of-service">
            {i18n.t.termsOfService}
          </A>{' '}
          {i18n.t.and}{' '}
          <A class="link" href="/privacy-policy">
            {i18n.t.privacyPolicy}
          </A>
          .
        </div>
      </Show>

      {/* <div class="mt-8 w-full gap-4">
        <p class="flex justify-center text-xl text-[var(--hl-400)]">{i18n.t.whyRegister}</p>
        <div class="flex flex-col items-center">
          <p>
            {i18n.t.youDontNeedToRegister}
          </p>
          <p>
            {i18n.t.ifYouChooseToRegister} {i18n.t.yourDataWillBeStored}
          </p>
        </div>
      </div> */}

    </Page>
  )
}

export default LoginPage

type FormProps = { isLoading: boolean }

const RegisterForm: Component<FormProps> = (props) => {
  const [store, setStore] = createStore({ handle: '', username: '', password: '', confirm: '' })
  const navigate = useNavigate()
  const register = () => {
    const { username, password, confirm, handle } = store

    if (!handle || !username || !password) return
    if (password !== confirm) {
      toastStore.warn(i18n.t.passwordsDoNotMatch, 2)
      return
    }

    userStore.register({ handle, username, password }, () => navigate('/profile'))
  }

  const i18n = i18nStore((s) => ({ t: s.t }))

  return (
    <form onSubmit={register} class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <TextInput
          label={i18n.t.displayName}
          placeholder={i18n.t.displayName}
          required
          onChange={(ev) => setStore('handle', ev.currentTarget.value)}
        />
        <TextInput
          label={i18n.t.username}
          fieldName="username"
          placeholder={i18n.t.username}
          required
          onChange={(ev) => setStore('username', ev.currentTarget.value)}
        />
        <TextInput
          label={i18n.t.password}
          placeholder={i18n.t.password}
          type="password"
          onChange={(ev) => setStore('password', ev.currentTarget.value)}
          required
        />
        <TextInput
          placeholder={i18n.t.confirmPassword}
          type="password"
          required
          onChange={(ev) => setStore('confirm', ev.currentTarget.value)}
          onKeyDown={(ev) => (ev.key === 'Enter' ? register() : null)}
        />
      </div>

      <Button disabled={props.isLoading} onClick={register}>
        {props.isLoading ? i18n.t.registering : i18n.t.register}
      </Button>
    </form>
  )
}

const LoginForm: Component<FormProps> = (props) => {
  let refGoogle: any
  const navigate = useNavigate()
  const [query] = useSearchParams()
  const loc = useLocation()
  const state = settingStore((s) => ({ config: s.config, initLoading: s.initLoading }))
  const user = userStore((s) => ({ loggedIn: s.loggedIn }))

  const [error, setError] = createSignal<string>()
  const google = useGoogleReady()

  const [store, setStore] = createStore({
    username: loc.pathname.includes('/remember') ? storage.localGetItem(ACCOUNT_KEY) || '' : '',
    password: '',
  })

  createEffect(() => {
    if (state.initLoading) return

    if (query.callback && isLoggedIn()) {
      for (const authUrl of state.config.authUrls) {
        if (query.callback.startsWith(authUrl)) return handleLogin()
      }
      setError(i18n.t.invalidCallbackUrl)
      return
    }
  })

  createEffect(
    on(
      () => google(),
      () => {
        const win: any = window
        const api = win.google?.accounts?.id

        if (!api) return

        if (state.config.serverConfig?.googleClientId) {
          api.initialize({
            client_id: state.config.serverConfig?.googleClientId,
            callback: (result: any) => {
              userStore.handleGoogleCallback('login', result, () => navigate('/dashboard'))
            },
          })

          api.renderButton(refGoogle, {
            theme: 'filled_black',
            size: 'large',
            type: 'standard',
            text: 'signin_with',
          })
        }
      }
    )
  )

  const handleLogin = () => {
    userStore.thirdPartyLogin((token) => {
      location.href = `${query.callback}?access_token=${token}`
    })
  }

  const login = () => {
    const { username, password } = store
    if (!username || !password) return

    userStore.login(username, password, (token) => {
      if (query.callback) {
        for (const authUrl of state.config.authUrls) {
          if (!query.callback.startsWith(authUrl)) continue
          wait(0.1).then(() => handleLogin())
          return
        }
      }

      if (query.callback) return
      navigate('/dashboard')
    })
  }

  const i18n = i18nStore((s) => ({ t: s.t }))

  return (
    <form class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <TextInput
          placeholder={i18n.t.username}
          disabled={user.loggedIn}
          required
          value={store.username}
          onChange={(ev) => setStore('username', ev.currentTarget.value)}
        />
        <TextInput
          placeholder={i18n.t.password}
          type="password"
          required
          disabled={user.loggedIn}
          onChange={(ev) => setStore('password', ev.currentTarget.value)}
          onKeyDown={(ev) => (ev.key === 'Enter' ? login() : null)}
        />
      </div>

      <Show when={error()}>
        <TitleCard type="rose">{error()}</TitleCard>
      </Show>

      <Button onClick={login} disabled={user.loggedIn || props.isLoading || !!error()}>
        {props.isLoading ? i18n.t.loggingIn : i18n.t.login}
      </Button>

      <div
        class="flex justify-center"
        classList={{ hidden: user.loggedIn }}
        ref={(ref) => {
          refGoogle = ref
        }}
        id="g_id_onload"
        data-context="signin"
        data-ux_mode="popup"
        data-login_uri={`${location.origin}/oauth/google`}
        data-itp_support="true"
      ></div>
    </form>
  )
}
